"use client";

import * as React from "react";
import { Suspense } from "react";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { type ThemeProviderProps } from "next-themes";
import { useEditorStore } from "@/store/theme-editor-store";
import { applyThemeToElement } from "@/lib/theme/utils/apply-theme";
import { usePrivy } from "@privy-io/react-auth";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { authFetch } from "@/lib/auth-fetch";


function ThemeSynchronizer() {
  const { themeState, setThemeState } = useEditorStore();
  const { resolvedTheme } = useTheme();
  const { user } = usePrivy();
  const [isLoaded, setIsLoaded] = React.useState(false);

  // Apply CSS variables whenever themeState changes
  React.useEffect(() => {
    const root = document.documentElement;
    if (!root) return;
    applyThemeToElement(themeState, root);
  }, [themeState]);

  // Sync resolvedTheme (light/dark) from next-themes to themeState
  React.useEffect(() => {
    if (resolvedTheme === "light" || resolvedTheme === "dark") {
      if (themeState.currentMode !== resolvedTheme) {
        setThemeState({ ...themeState, currentMode: resolvedTheme });
      }
    }
  }, [resolvedTheme, themeState, setThemeState]);

  // Load theme from backend on mount
  React.useEffect(() => {
    if (!user) return;
    
    const fetchTheme = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';
        const res = await authFetch(`${backendUrl}/api/theme/${user.id}`);
        if (res.ok) {
          const themeData = await res.json();
          if (themeData && Object.keys(themeData).length > 0) {
            // Merge backend theme with current state to ensure valid structure
            // Prioritize backend data
            setThemeState({ ...themeState, ...themeData });
          }
        }
      } catch (error) {
        console.error("Failed to load theme:", error);
      } finally {
        setIsLoaded(true);
      }
    };
    
    fetchTheme();
  }, [user]); // Run when user changes (e.g. login)

  // Save theme to backend when changed (debounced)
  const saveTheme = useDebouncedCallback(async (state: any) => {
    if (!user || !isLoaded) return;
    
    try {
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:8000';
      await authFetch(`${backendUrl}/api/theme/${user.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ theme: state }),
      });
    } catch (error) {
      console.error("Failed to save theme:", error);
    }
  }, 2000); // 2 second debounce

  React.useEffect(() => {
    if (isLoaded) {
      saveTheme(themeState);
    }
  }, [themeState, isLoaded, saveTheme]);

  return null;
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider {...props}>
      <Suspense fallback={null}>
        <ThemeSynchronizer />
      </Suspense>
      {children}
    </NextThemesProvider>
  );
}

export { useTheme };
